/**
 * One-shot native platform initialisation. — v147.99.39
 *
 * Runs at app boot (from `index.js`). Safe on web — every call is behind
 * `Capacitor.isNativePlatform()`, so bundling on web is a no-op.
 *
 * Responsibilities:
 *   1. Status bar — overlay the WebView + set dark content (since our app
 *      background is cream) so the notch/dynamic island area is transparent
 *      and CSS `env(safe-area-inset-top)` on our top bars handles spacing.
 *   2. Add a `.is-native` class + `.platform-android` / `.platform-ios`
 *      to <html> so CSS can target native-only polish and safe-area padding.
 *   3. v147.99.39 — Intercept Android hardware back / swipe-back so the
 *      user isn't kicked out to the marketing landing when they swipe
 *      from `/app/dashboard`. Behavior mirrors Instagram / WhatsApp:
 *        · Deep-inside the app → history.back()
 *        · At the dashboard root → App.exitApp() (minimizes to home)
 *      Without this hook, Capacitor's default back-button dispatcher
 *      pops WebView history all the way to the initial URL — which was
 *      `/` on first launch (before auth) — and renders the marketing
 *      landing with a Sign-in button (visually indistinguishable from a
 *      logged-out state, even though the session cookie is still valid).
 */
import { Capacitor } from "@capacitor/core";

// Any path that starts with one of these prefixes is considered a "root"
// where back-swipe should minimize the app instead of navigating away
// (mirrors the Android convention: back at home minimizes, doesn't quit).
const ROOT_PATHS = [
  "/app",
  "/app/",
  "/app/dashboard",
  "/admin",
  "/admin/",
  "/",
];

function isRootPath(pathname) {
  if (!pathname) return true;
  // Exact match — treat /app and /app/ as identical to /app/dashboard
  // for the "back at home minimizes" rule.
  const p = pathname.replace(/\/+$/, "") || "/";
  return ROOT_PATHS.some((rp) => {
    const nrp = rp.replace(/\/+$/, "") || "/";
    return p === nrp;
  });
}

export async function initNative() {
  if (!Capacitor.isNativePlatform()) return;
  document.documentElement.classList.add("is-native");
  document.documentElement.classList.add(`platform-${Capacitor.getPlatform()}`);

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    // Overlay = true → web view starts at Y=0 (edge-to-edge). Our CSS uses
    // `env(safe-area-inset-top)` on the sticky top bar to push content below
    // the status bar / dynamic island.
    await StatusBar.setOverlaysWebView({ overlay: true });
    // Dark icons on light background.
    await StatusBar.setStyle({ style: Style.Dark });
  } catch (e) {
    // Plugin missing (older builds) — non-fatal.
    console.warn("[nativeInit] StatusBar setup skipped:", e?.message);
  }

  // v147.99.39 — Android back-button / swipe-back interception.
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", () => {
      const path = window.location.pathname || "/";
      if (isRootPath(path)) {
        // At the dashboard / landing root — minimize the app instead of
        // clearing the WebView back to the auth page.
        try { App.exitApp(); } catch { /* older shells: no-op */ }
        return;
      }
      // Deep-linked screen — let the SPA handle a back navigation.
      // history.back() triggers react-router which pops one level.
      window.history.back();
    });
  } catch (e) {
    console.warn("[nativeInit] back-button listener skipped:", e?.message);
  }
}
